import React, { useState, createContext, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";

const DialogContext = createContext();

export function Dialog({ open, onClose, children, className = "", width }) {
  const dialogWidth = width || "max-w-2xl w-full";
  const [confirmData, setConfirmData] = useState(null);

  const handleActionClick = (child) => {
    const confirmText = child.props.confirm;
    if (confirmText && confirmText !== "") {
      setConfirmData({
        message: confirmText,
        onConfirm: child.props.onClick,
      });
    } else {
      child.props.onClick?.();
    }
  };

  const dialogVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { type: "spring", stiffness: 300, damping: 25 } },
    exit: { opacity: 0, scale: 0.95, transition: { duration: 0.2 } },
  };

  return (
    <DialogContext.Provider value={{ handleActionClick }}>
      <AnimatePresence>
        {open && (
          <div className="fixed inset-0 z-[500] flex justify-center items-center overflow-hidden px-4">
            {/* Backdrop */}
            <div
    className="absolute inset-0 bg-black/40 backdrop-blur-sm"
    onClick={onClose}
  />

            {/* Dialog box */}
            <motion.div
              className={`relative bg-white rounded-xs shadow-2xl ${dialogWidth} max-h-[90vh] flex flex-col overflow-hidden ${className}`}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              variants={dialogVariants}
            >
              {children}
            </motion.div>

            {/* Confirm Modal */}
            <AnimatePresence>
              {confirmData && (
                <motion.div
                  className="fixed inset-0 z-[600] flex items-center justify-center bg-black/50"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <motion.div
                    className="bg-white rounded-sm shadow-sm w-full max-w-sm p-6"
                    initial={{ scale: 0.85, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.85, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  >
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Potwierdzenie</h3>
                    <p className="text-sm text-gray-600 mb-6">{confirmData.message}</p>
                    <div className="flex justify-end gap-3">
                      <button
                        onClick={() => setConfirmData(null)}
                        className="px-4 py-2 text-sm rounded border border-gray-300 hover:bg-gray-100 transition"
                      >
                        Anuluj
                      </button>
                      <button
                        onClick={() => {
                          confirmData.onConfirm?.();
                          setConfirmData(null);
                        }}
                        className="px-4 py-2 text-sm rounded bg-red-600 text-white hover:bg-red-700 transition"
                      >
                        Potwierdź
                      </button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </AnimatePresence>
    </DialogContext.Provider>
  );
}

// Nagłówek dialogu
export function DialogTitle({ children, className = "" }) {
  return (
    <div
      className={`sticky top-0 z-10 px-6 py-4 bg-gradient-to-r from-primary-600 via-primary-500 to-primary-600 text-white font-semibold text-normal shadow-md ${className}`}
    >
      {children}
    </div>
  );
}

// Zawartość scrollowalna
export function DialogContent({ children, className = "" }) {
  return (
    <div
      className={`flex-1 overflow-y-auto px-6 py-4 scrollbar-thin scrollbar-thumb-blue-400 scrollbar-track-transparent hover:scrollbar-thumb-blue-500 ${className}`}
    >
      {children}
    </div>
  );
}

// Akcje dolne z potwierdzeniem
export function DialogActions({ children, className = "" }) {
  const { handleActionClick } = useContext(DialogContext);

  return (
    <div
      className={`sticky bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-4 flex flex-col sm:flex-row justify-end gap-3 ${className}`}
    >
      {React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) return child;
        return React.cloneElement(child, {
          onClick: () => handleActionClick(child),
        });
      })}
    </div>
  );
}
